import { DateTime } from 'luxon';

import { stripHtmlTags } from '@poseidon/shared-utils/html';
import type { LinkableHistoryAction } from '@poseidon/types/types/stepResolution.type';
import {
  isHistoryActionLinkableToStep,
  isLinkableHistoryAction,
  isStepManuallyResolvable,
  LINKABLE_HISTORY_ACTIONS_BY_STEP_TYPE,
} from '@poseidon/types/types/stepResolution.type';

import type { CadenceStepStatePure, CadenceStepTypePure } from '../CGStep/types';

/** How a rep closes out a step by hand. */
export type StepResolutionState = 'finished' | 'skipped';

/**
 * A rep closing out a step by hand. `interactionId` is the public id of an existing history row
 * (contact_user_interaction) to attribute to the step — e.g. the LinkedIn message they sent
 * outside the automation.
 */
export type StepResolution = { state: StepResolutionState; interactionId?: string };

/**
 * Picker labels for the history actions a rep can attribute to a step. Which actions are linkable,
 * and to which step types, is decided by `LINKABLE_HISTORY_ACTIONS_BY_STEP_TYPE` (shared with the
 * server, which enforces it); the `Record` makes a newly linkable action without a label a type error.
 */
const LINKABLE_HISTORY_ACTION_LABELS: Record<LinkableHistoryAction, string> = {
  connection_sent: 'Connection request',
  message_sent: 'LinkedIn message',
  inmail_sent: 'InMail',
  email_sent: 'Email',
  phone_call_sent: 'Call',
  phone_received: 'Call received',
  visit: 'Profile visit',
  follow: 'Follow',
  like_posts: 'Liked posts',
  comment_on_post: 'Comment',
  reminder_completed: 'Reminder done',
};

/** A history row offered in the "link a history item" picker. */
export type StepHistoryOption = {
  /** contact_user_interaction.public_id */
  id: string;
  /** What the row records — decides which step types it can be linked to. */
  action: LinkableHistoryAction;
  /** One line: what happened, when, and a preview of what was sent. */
  label: string;
};

/** The history fields the picker needs — both surfaces' history rows carry these. */
export type StepHistorySource = {
  /** contact_user_interaction.public_id — absent on rows that aren't real history (e.g. queued sends). */
  publicId: string | undefined;
  action: string;
  date: Date;
  subject?: string | null;
  body?: string | null;
};

/** Whether a rep can still close this step out by hand — the same rule the server enforces. */
export function isStepResolvable(step: { state?: CadenceStepStatePure; type: CadenceStepTypePure }): boolean {
  return isStepManuallyResolvable(step);
}

const MAX_PREVIEW = 60;

function formatHistoryDate(date: Date, now: Date): string {
  const dt = DateTime.fromJSDate(date);
  return dt.hasSame(DateTime.fromJSDate(now), 'year') ? dt.toFormat('LLL d, h:mm a') : dt.toFormat('LLL d, yyyy');
}

function previewText(subject: string | null | undefined, body: string | null | undefined): string {
  const raw = subject != null && subject.trim().length > 0 ? subject : (body ?? '');
  const text = stripHtmlTags(raw).replace(/\s+/g, ' ').trim();
  return text.length > MAX_PREVIEW ? `${text.slice(0, MAX_PREVIEW - 1).trimEnd()}…` : text;
}

/**
 * The contact's history rows a rep can link to some step they're closing out, newest first, as
 * one-line labels ("LinkedIn message · Sep 24, 3:02 PM · Hey John, …"). Narrow to one step with
 * `historyOptionsForStepType`.
 */
export function toStepHistoryOptions(history: StepHistorySource[], now: Date = new Date()): StepHistoryOption[] {
  const options: { option: StepHistoryOption; time: number }[] = [];
  for (const h of history) {
    if (!isLinkableHistoryAction(h.action) || h.publicId == null || h.publicId.length === 0) continue;
    const preview = previewText(h.subject, h.body);
    const parts = [LINKABLE_HISTORY_ACTION_LABELS[h.action], formatHistoryDate(h.date, now)];
    if (preview.length > 0) parts.push(preview);
    options.push({ option: { id: h.publicId, action: h.action, label: parts.join(' · ') }, time: h.date.getTime() });
  }
  return options.sort((a, b) => b.time - a.time).map((o) => o.option);
}

/** Whether any history row could ever be linked to a step of this type (sift / switch_campaign can't). */
export function canLinkHistoryToStepType(stepType: CadenceStepTypePure): boolean {
  return LINKABLE_HISTORY_ACTIONS_BY_STEP_TYPE[stepType].length > 0;
}

/** The options whose action matches the step's type — a call step only offers calls. */
export function historyOptionsForStepType(
  options: StepHistoryOption[],
  stepType: CadenceStepTypePure,
): StepHistoryOption[] {
  return options.filter((o) => isHistoryActionLinkableToStep(stepType, o.action));
}
